import { useTranslation } from "react-i18next";
import Modal from "../../../Modal";
import { CSSProperties, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { QueryLookupTablesApi } from "../../../../utils/QueryHelper";
import i18n from "../../../../i18n";
import { Fieldset } from "@trussworks/react-uswds";
import { toggleItemsInFilterList } from "../../../../utils/filters/shared";
import { AnalyticsAction, logEvent } from "../../../../utils/analytics";
import { SearchFilters } from "../../../../types/v1_schemas";
import FilterCheckbox from "../FilterCheckbox";
import { CrisisContext } from "../../../../App";

const ModalStyle: CSSProperties = {
  width: "80%",
  height: "80%",
  background: "white",
  display: "block",
  padding: "1rem",
  overflowY: "scroll",
  borderRadius: "10px"
};

const ButtonContainer: CSSProperties = {
  position: "sticky",
  bottom: "-1rem",
  height: "4rem",
  width: "100%",
  textAlign: "center",
  paddingTop: ".75rem",
  borderTop: "solid 1px black",
  backgroundColor: "white"
};

type ApiServiceProvided = {
  id: number;
  categoryId: number;
  plainLanguageEn: string;
  plainLanguageEs: string;
  textEn: string;
  textEs: string;
};

type ServiceCategory = {
  id: number;
  text: string;
  description: string;
};

export type ResponseServiceProvided = {
  id: string;
  categoryId: number;
  categoryName: string;
  plainLanguage: string;
  text: string;
};

type MappedServiceProvided = {
  categoryId: number;
  categoryName: string;
  services: ResponseServiceProvided[];
}

type InputProps = {
  count: number;
  isOpen: boolean;
  toggle: () => void;
  filters: SearchFilters;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
};

export default function MoreOptionsPopup({ count, isOpen, toggle, filters, setFilters }: InputProps) {
  const { t } = useTranslation();
  const [groupedProviders, setGroupedProviders] = useState([] as MappedServiceProvided[]);
  
  const { crisisSelectedValues, setCrisisSelectedValues } = useContext(CrisisContext);
  
  const setServiceProvidedFilter = (serviceProvided: ResponseServiceProvided) => {
      logEvent(AnalyticsAction.UpdateFilter, {
        label: "type of help",
        filter_type: "type of help",
        filter_value: serviceProvided.text,
      });
      setFilters({
        ...filters,
        serviceProvidedIds: toggleItemsInFilterList(
          filters.serviceProvidedIds as string[], 
          serviceProvided.id.toString().split(",")
          ) as [],
      });
      if (serviceProvided.categoryId === 1500103) {// Crisis services category
            setCrisisSelectedValues(
              toggleItemsInFilterList(
                crisisSelectedValues as string[],
                serviceProvided.id.split(",")
              )
            );
          }
    };

  function buttonContent() {
    return (
      <div style={ButtonContainer}>
        <button className="usa-button" onClick={toggle}>{t("viewXProviders").replace("##", count.toString())}</button>
      </div>
    );
  }

  const excludedServices: string[] = ["Adult eating disorder treatment (18+)", "Tratamiento de trastornos alimenticios en adultos (mayores de 18 años)", 
    "Eating disorder treatment – outpatient (care without staying in the hospital)", "Tratamiento de trastornos alimenticios: ambulatorio (atención médica sin hospitalización)", 
    "Eating disorder treatment - overnight within a hospital setting", "Tratamiento de trastornos alimenticios: nocturno en un entorno hospitalario", 
    "Eating disorder treatment - residential (overnight care)", "Tratamiento de trastornos alimenticios: residencial (atención médica nocturna)", 
    "Eating disorder treatment – structured outpatient (planned program of regular care without staying in the hospital)", "Tratamiento de trastornos alimenticios: ambulatorio (atención médica sin hospitalización)", 
    "Eating disorder treatment – structured outpatient (planned program of regular care with partial stay in the hospital)", "Tratamiento de trastornos alimenticios: ambulatorio estructurado (programa planificado de atención médica regular con hospitalización parcial)", 
    "Youth eating disorder treatment", "Tratamiento de trastornos alimenticios en jóvenes", "Psychedelic-assisted_therapies", "Ketamine-assisted_therapy"];

  const responseFn = (response: AxiosResponse) => {
    let categories = (response.data?.serviceCategories as ServiceCategory[]).filter((category: ServiceCategory) => category.text !== "Unknown" && category.text !== "");
    let responseServices: ResponseServiceProvided[] = []; 
    response.data?.servicesProvided
      .filter((service: ApiServiceProvided) => categories.map((category: ServiceCategory) => category.id).includes(service.categoryId) && service.textEn && service.textEs)
      .filter((service: ApiServiceProvided) => !excludedServices.includes(service.plainLanguageEn) && !excludedServices.includes(service.plainLanguageEs))
      .forEach((service: ApiServiceProvided) => {
        let index = -1;
        if (responseServices.length > 0)
          index = responseServices.findIndex(s => 
              (s.text === (i18n.language === "es" ? service.textEs : service.textEn)) && 
              (s.categoryId === service.categoryId));
        if (index === -1) {
          responseServices.push({
            id: service.id.toString(),
            categoryId: service.categoryId,
            plainLanguage: i18n.language === "es" ? service.plainLanguageEs : service.plainLanguageEn,
            text: i18n.language === "es" ? service.textEs : service.textEn,
            categoryName: categories.find(cat => cat.id === service.categoryId)?.text || ""
          });
        } else {
          responseServices[index].id += `,${service.id.toString()}`;
        }
      }, [])
    let mappedServices: MappedServiceProvided[] = [];
    responseServices.forEach((service: ResponseServiceProvided) => {
      let categoryIndex = mappedServices.findIndex(m => m.categoryId === service.categoryId);
      if (categoryIndex === -1) { // category not added yet
        mappedServices.push({
          categoryId: service.categoryId,
          categoryName: service.categoryName,
          services: [service]
        });
      } else { // category already added, add service to existing category
        mappedServices[categoryIndex].services.push(service);
      }
    });
    setGroupedProviders(mappedServices);
  }

  const errorFn = (error: any) => {
    console.error("Error fetching services provided:", error);
  }
  
  useEffect(() => {
    QueryLookupTablesApi(
      {
        IncludeServicesProvided: true,
        IncludeServiceCategories: true,
        language: i18n.language
      },
      new AbortController().signal,
      responseFn, errorFn);
  }, [i18n.language]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} buttonContent={buttonContent()} boxStyling={ModalStyle}>
      <Fieldset legend={t("typeOfHelpTitle")} legendStyle={"large"}>
        {groupedProviders.sort((a, b) => a.categoryName.localeCompare(b.categoryName)).map((category: MappedServiceProvided) => { return (
          <div key={category.categoryId} className="more-options-category">
            <h3>{category.categoryName}</h3>
            {category.services.sort((a, b) => a.text.localeCompare(b.text)).map((service: ResponseServiceProvided) => {
              return (
              <div key={service.plainLanguage} className="service-columns" >
                <FilterCheckbox
                  name={service.id.toString()}
                  value={service.id.toString()}
                  label={service.text}
                  customChecked={() => {return (filters.serviceProvidedIds ?? []).some(id => service.id.split(",").includes(id))}}
                  selectedFilterValues={filters.serviceProvidedIds ?? []}
                  onChange={() => setServiceProvidedFilter(service)}
                  key={service.id.toString()}
                />
              </div>
            )})}
          </div>
        )})}
      </Fieldset>
    </Modal>
  );
}